import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { usePickThree } from "../../hooks/usePickThree";
import { usePickThreeStanding } from "../../hooks/usePickThreeStanding";
import { usePickThreeSeason } from "../../hooks/usePickThreeSeason";
import { ChevronRight } from "lucide-react";
import BrandWordmark from "../../components/BrandWordmark";
import { buildGamesBySlug } from "../../utils/gameStatus";
import { describeSpread, describeTeamWeek } from "../../games/higherLower";
import {
  buildPickRows, canMakeCaptain, inviteText, CAPTAIN_RULE, clearDraft, describeWindow, droppedNote, dropLockedPicks, GAME_NAME, isRowLocked,
  loadDraft, LOCK_RULE, pickWeek, scoreEntry, scoreLine, seasonSummary, shareText, standingBand, STANDING_WAITING, weekWindow,
} from "../../games/pickThree";
import { GameMessage, GamePage, Helmet, PRIMARY_BUTTON, ShareResult } from "../../games/GameParts";
import { InviteButton, PickThreeBackdrop, signed } from "../../components/PickThreeParts";
import { PickBar, PickList, useLockClock } from "../../components/PickThreeList";

// Pick Three. The rules and the scoring live in games/pickThree.js and the
// locks in migration 084; this is the screen. While any team has still to lock
// it is the picker, with points showing on picks whose games have started.
// Once the last team has locked it is the result.

const LABEL = { fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--color-text-faint)", margin: "0 0 6px" };

// The header bar: "Pick Three by" and the Lineup wordmark, in the cut that
// reads on the current theme. The week is in the hero below, not here.
const TITLE = (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    {GAME_NAME}
    <span style={{ fontSize: 12, fontWeight: 600, textTransform: "none", letterSpacing: 0, color: "var(--color-text-muted)" }}>by</span>
    <BrandWordmark width={62} alt="Lineup" />
  </span>
);

const sameSet = (a, b) => a.length === b.length && a.every((slug) => b.includes(slug));

/** The week and its locks, on the photo. */
function Hero({ week, times, phase, onSeason }) {
  return (
    <PickThreeBackdrop style={{ margin: "-16px -16px 16px", padding: "18px 16px" }}>
      <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: "-0.01em" }}>Week {week}</div>
      {times && (
        <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.55, color: "rgba(255,255,255,0.88)" }}>
          <div><b style={{ color: "#fff" }}>Starts</b> {times.starts}</div>
          {phase === "open" && <div><b style={{ color: "#fff" }}>Last picks</b> {times.last}</div>}
          {phase === "open" && <div style={{ marginTop: 6, fontSize: 12, color: "rgba(255,255,255,0.75)" }}>{LOCK_RULE}</div>}
          {onSeason && (
            <button
              type="button"
              onClick={onSeason}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 10, padding: 0, border: "none", background: "none", font: "inherit", fontSize: 13, fontWeight: 700, color: "#7dffa3", cursor: "pointer" }}
            >
              Your season and past weeks <ChevronRight size={15} aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </PickThreeBackdrop>
  );
}

function Picker({ week, rows, teams, entry, save, now }) {
  const [selected, setSelected] = useState(entry?.teams || []);
  const [captain, setCaptain] = useState(entry?.captain || null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const gamesBySlug = useMemo(() => buildGamesBySlug(rows), [rows]);
  const all = useMemo(() => buildPickRows(teams, gamesBySlug, week), [teams, gamesBySlug, week]);
  const bySlug = useMemo(() => Object.fromEntries(all.map((r) => [r.slug, r])), [all]);
  const isLocked = (slug) => isRowLocked(bySlug[slug], now);

  const saved = Boolean(entry) && sameSet(selected, entry.teams) && captain === entry.captain;
  const full = selected.length === 3;
  const lockedPicks = selected.filter(isLocked);

  // Points under each pick whose game has started (double for the captain);
  // nothing under the rest.
  const score = useMemo(() => scoreEntry(teams, selected, week, captain), [teams, selected, week, captain]);
  const barScores = selected.map((slug, i) => (isLocked(slug) ? score.rows[i] : undefined));

  // A visitor who chose teams on the signed-out page and then signed up has a
  // draft waiting. Save it once, here, so they land on picks that are already
  // in. An entry already on the account wins over a draft. A team that locked
  // while they were signing up is taken out, and they are told which, rather
  // than having the whole save refused. A draft with no captain waits for one.
  const adopted = useRef(false);
  useEffect(() => {
    if (adopted.current) return;
    adopted.current = true;
    const draft = loadDraft(week);
    if (!draft) return;
    clearDraft();
    if (entry) return;
    const { kept, dropped } = dropLockedPicks(draft.teams, all, new Date());
    const keptCaptain = kept.includes(draft.captain) ? draft.captain : null;
    setSelected(kept);
    setCaptain(keptCaptain);
    if (dropped.length) { setMessage({ error: true, text: droppedNote(dropped) }); return; }
    if (kept.length !== 3 || !keptCaptain) return;
    setSaving(true);
    save(kept, keptCaptain).then((problem) => {
      setSaving(false);
      setMessage(problem ? { error: true, text: problem } : { error: false, text: "Your picks are saved. You can change any of them until that team locks." });
    });
  }, [week, entry, save, all]);

  const toggle = (slug) => {
    if (isLocked(slug)) return;
    setMessage(null);
    if (selected.includes(slug)) {
      setSelected(selected.filter((s) => s !== slug));
      if (captain === slug) setCaptain(null);
    } else if (selected.length < 3) {
      setSelected([...selected, slug]);
    }
  };

  const canCaptain = (slug) => canMakeCaptain(captain, slug, isLocked);
  const makeCaptain = (slug) => {
    if (!canCaptain(slug)) return;
    setMessage(null);
    setCaptain(slug);
  };

  const onSave = async () => {
    setSaving(true);
    const problem = await save(selected, captain);
    setSaving(false);
    setMessage(problem ? { error: true, text: problem } : { error: false, text: "Picks saved. You can change any of them until that team locks." });
  };

  let label = `Pick ${3 - selected.length} more`;
  if (full && !captain) label = "Choose a captain";
  else if (full) label = saving ? "Saving" : lockedPicks.length === 3 && saved ? "All three picks are locked in" : saved ? "Picks saved" : "Save picks";
  const disabled = !full || !captain || saved || saving;

  return (
    <>
      <p style={{ fontSize: 13, lineHeight: 1.5, color: "var(--color-text-strong)", margin: "0 0 12px" }}>
        Pick the three teams you think score the most Lineup points this week, then make one your captain. Any FBS team,
        rostered or not. The number on the right is the spread: a big favourite does not always score the most.
      </p>
      <PickList rows={all} selected={selected} onToggle={toggle} now={now} />
      <PickBar
        selected={selected} teams={teams} scores={barScores} locked={lockedPicks}
        captain={captain} onCaptain={makeCaptain} canCaptain={canCaptain} onRemove={toggle}
      >
        {message ? (
          <p role="status" style={{ fontSize: 12, margin: "8px 0 0", textAlign: "center", color: message.error ? "var(--color-danger-text)" : "var(--color-success-text)" }}>
            {message.text}
          </p>
        ) : full && !captain && (
          <p style={{ fontSize: 12, margin: "8px 0 0", textAlign: "center", color: "var(--color-text-strong)" }}>{CAPTAIN_RULE}</p>
        )}
{/* Once the picks are saved and unchanged, the button's job is done, so
            it becomes the invite rather than a disabled "Picks saved". */}
        {saved && !saving ? (
          <>
            {!message && <p style={{ fontSize: 12, margin: "8px 0 0", textAlign: "center", color: "var(--color-success-text)" }}>Your picks are saved.</p>}
            <InviteButton text={inviteText(week, selected, captain, bySlug)} style={{ marginTop: 8 }} />
          </>
        ) : (
          <button
            type="button"
            onClick={onSave}
            disabled={disabled}
            style={{ ...PRIMARY_BUTTON, marginTop: 8, opacity: disabled ? 0.5 : 1, cursor: disabled ? "default" : "pointer" }}
          >
            {label}
          </button>
        )}
      </PickBar>
    </>
  );
}

function ScoreList({ title, rows, gamesBySlug, week, teams, footnote = null }) {
  return (
    <div style={{ marginTop: 16 }}>
      <p style={LABEL}>{title}</p>
      <div style={{ border: "1.5px solid var(--color-bg-subtle)", borderRadius: 12, overflow: "hidden" }}>
        {rows.map((r, i) => {
          const detail = describeTeamWeek(r.slug, teams, gamesBySlug, week);
          const spread = detail ? describeSpread(detail.spread) : null;
          return (
            <div key={r.slug} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderTop: i ? "1px solid var(--color-border)" : "none", backgroundColor: r.inBest ? "var(--color-success-bg)" : "transparent" }}>
              <Helmet team={r} size={32} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "var(--color-text-strong)" }}>{r.name}{r.tied ? "*" : ""}</span>
                {detail && (
                  <span style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)" }}>
                    {detail.result} {detail.isHome ? "vs" : "at"} {detail.opponentName}{spread ? `. ${spread}.` : ""}
                  </span>
                )}
                {r.isCaptain && (
                  <span style={{ display: "block", fontSize: 12, fontWeight: 700, color: "var(--color-captain-text)" }}>
                    Captain{r.points != null ? `: ${signed(r.points)} counted twice` : ", counts double"}
                  </span>
                )}
              </span>
              <span style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: r.scored == null ? "var(--color-text-faint)" : r.scored < 0 ? "var(--color-danger-text)" : "var(--color-text-strong)" }}>
                {r.scored == null ? "—" : signed(r.scored)}
              </span>
            </div>
          );
        })}
      </div>
      {footnote && <p style={{ fontSize: 12, lineHeight: 1.5, color: "var(--color-text-muted)", margin: "6px 0 0" }}>{footnote}</p>}
    </div>
  );
}

/**
 * Where the player finished: the exact share of other players they beat, ties
 * counting half. A percentage, never a player count or anyone else's score.
 */
function Standing({ standing }) {
  if (!standing) return null;
  const band = standingBand(standing);
  if (!band) return <p style={{ fontSize: 12, color: "var(--color-text-muted)", textAlign: "center", margin: "12px 0 0" }}>{STANDING_WAITING}</p>;
  return (
    <div style={{ textAlign: "center", marginTop: 16, padding: "12px 0", borderTop: "1px solid var(--color-border)", borderBottom: "1px solid var(--color-border)" }}>
      <p style={LABEL}>Where you finished</p>
      <div style={{ fontSize: 28, fontWeight: 900, color: "var(--color-text-strong)" }}>Better than {band.beatPct}%</div>
      <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 2 }}>of everyone who played this week</div>
    </div>
  );
}

/**
 * "Your season", under a week's result: weeks played, best week and average
 * points, then a row per week with the points scored and the best possible.
 * No placings here: the week's own result shows that.
 * A row opens that week's result. Only this player's numbers, and no player
 * counts. Shown once there are two weeks to compare.
 */
function Season({ teams, viewing, current, onOpen }) {
  const { entries, standings } = usePickThreeSeason(current);
  const season = useMemo(() => seasonSummary(entries, teams, standings, current), [entries, teams, standings, current]);
  if (season.played < 2) return null;

  const stat = (value, label) => (
    <div style={{ backgroundColor: "var(--color-bg-subtle)", borderRadius: 12, padding: "8px 6px", textAlign: "center" }}>
      <b style={{ display: "block", fontSize: 18, fontWeight: 900, color: "var(--color-text-strong)" }}>{value}</b>
      <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{label}</span>
    </div>
  );

  return (
    <section aria-labelledby="p3-season" style={{ marginTop: 20 }}>
      <p id="p3-season" style={LABEL}>Your season</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, marginBottom: 6 }}>
        {stat(season.played, "weeks played")}
        {stat(signed(season.best.total), `best, week ${season.best.week}`)}
        {stat(signed(season.average), "average")}
      </div>
      {season.rows.map((r) => {
        const here = r.week === viewing;
        return (
          <button
            key={r.week}
            type="button"
            onClick={() => onOpen(r.week)}
            aria-current={here ? "page" : undefined}
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "calc(100% + 32px)", margin: "0 -16px", padding: "10px 16px",
              border: "none", borderTop: "1px solid var(--color-border)", font: "inherit", textAlign: "left", cursor: "pointer",
              backgroundColor: here ? "var(--color-band-bg)" : "transparent", color: "var(--color-text-strong)",
            }}
          >
            <span style={{ width: 62, fontSize: 14, fontWeight: 700 }}>Week {r.week}</span>
            <span style={{ flex: 1, fontSize: 14, fontVariantNumeric: "tabular-nums" }}>
              <b>{signed(r.total)}</b> <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>of {r.bestTotal}</span>
            </span>
            <ChevronRight size={16} aria-hidden="true" color="var(--color-text-faint)" />
          </button>
        );
      })}
    </section>
  );
}

function Result({ week, phase, entry, teams, rows, onDone, current, onOpenWeek }) {
  const gamesBySlug = useMemo(() => buildGamesBySlug(rows), [rows]);
  const score = useMemo(() => (entry ? scoreEntry(teams, entry.teams, week, entry.captain) : null), [teams, entry, week]);
  const standing = usePickThreeStanding(week, Boolean(entry));

  if (!score) {
    return (
      <>
        <GameMessage>
          You did not make picks for week {week}. {phase === "final" ? "Check back when the next week opens on Sunday at 10am ET." : "Every team has now locked. The next week opens Sunday at 10am ET."}
        </GameMessage>
        <button type="button" onClick={onDone} style={{ ...PRIMARY_BUTTON, marginTop: 12 }}>Done</button>
      </>
    );
  }

  const share = Math.max(0, Math.min(1, score.bestTotal > 0 ? score.total / score.bestTotal : 0));
  const final = phase === "final";
  return (
    <>
      <div style={{ textAlign: "center", padding: "6px 0 4px" }}>
        <div style={{ fontSize: 52, fontWeight: 900, lineHeight: 1, color: "var(--color-text-strong)", fontVariantNumeric: "tabular-nums" }}>{score.total}</div>
        <div style={{ fontSize: 14, color: "var(--color-text-muted)", marginTop: 6 }}>{scoreLine(score)}</div>
        <div style={{ height: 8, borderRadius: 6, backgroundColor: "var(--color-bg-subtle)", margin: "12px 20px 0", overflow: "hidden" }}>
          <div style={{ width: `${share * 100}%`, height: "100%", backgroundColor: "var(--color-success)" }} />
        </div>
        {!final && <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 10 }}>Not final. Points update as games finish.</div>}
      </div>

      <Standing standing={standing} />
      <ScoreList title="Your three" rows={score.rows} gamesBySlug={gamesBySlug} week={week} teams={teams} />
      <ScoreList
        title="Best three"
        rows={score.best.map((b, i) => ({
          // The best possible three captains the top scorer.
          slug: b.slug, name: b.name, logo: b.logo, points: b.points, isCaptain: i === 0, scored: i === 0 ? b.points * 2 : b.points,
          inBest: score.rows.some((r) => r.slug === b.slug),
          // Marked when other teams scored the same: any of them would have counted.
          tied: score.alsoBest.length > 0 && b.points === score.cutoff,
        }))}
        footnote={score.alsoBest.length
          ? `* Also scored ${signed(score.cutoff)}, and would have counted the same: ${score.alsoBest.map((t) => t.name).join(", ")}.`
          : null}
        gamesBySlug={gamesBySlug}
        week={week}
        teams={teams}
      />

      <p style={{ fontSize: 12, lineHeight: 1.5, color: "var(--color-text-muted)", margin: "14px 0" }}>
        The captain's points count twice. Late results can still move a score until every game is final.
      </p>
      <div style={{ backgroundColor: "var(--color-bg-subtle)", borderRadius: 12, padding: "10px 12px", fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-line", color: "var(--color-text-strong)", marginBottom: 12 }}>
        {shareText(score, week, standing)}
      </div>
      <ShareResult getText={() => shareText(score, week, standing)} />
      <Season teams={teams} viewing={week} current={current} onOpen={onOpenWeek} />
    </>
  );
}

function PickThreeGame() {
  const { seasonConfig } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const current = pickWeek(seasonConfig?.currentWeek);

  // ?week= opens an earlier week's result, from the card's "Last week" line.
  const asked = parseInt(params.get("week") || "", 10);
  const week = current !== null && Number.isInteger(asked) && asked >= 1 && asked < current ? asked : current;

  // `teams` carries live points while games are on (usePickThree).
  const { rows, entries, failed, save, teams } = usePickThree(week, { finished: current !== null && week < current });
  // Ticks, so the page moves from picker to result at the last lock, and the
  // countdowns stay true, without a reload.
  const now = useLockClock();
  // The latest earlier week this player entered: where "Your season" opens
  // from the picker, since the season block sits under a finished result.
  const { entries: seasonEntries } = usePickThreeSeason(current);
  const pastWeek = useMemo(() => {
    const weeks = Object.keys(seasonEntries || {}).map(Number).filter((w) => current !== null && w < current);
    return weeks.length ? Math.max(...weeks) : null;
  }, [seasonEntries, current]);
  const weekState = useMemo(() => (rows ? weekWindow(rows, teams, now) : null), [rows, teams, now]);
  const done = () => navigate("/home");

  if (week === null) {
    return <GamePage title={TITLE}><GameMessage>Pick Three opens with the first week of games.</GameMessage></GamePage>;
  }
  if (!rows || !entries || !weekState) {
    return <GamePage title={TITLE}><GameMessage>{failed ? "Your picks could not be loaded. Try again shortly." : `Loading week ${week}.`}</GameMessage></GamePage>;
  }
  if (weekState.phase === "unavailable") {
    return <GamePage title={TITLE}><GameMessage>Week {week} opens once its schedule is in.</GameMessage></GamePage>;
  }

  const times = describeWindow(weekState);
  const entry = entries[week];
  return (
    <GamePage title={TITLE}>
      <Hero
        week={week} times={times} phase={weekState.phase}
        onSeason={weekState.phase === "open" && week === current && pastWeek !== null ? () => navigate(`/games/pick-three?week=${pastWeek}`) : null}
      />
      {weekState.phase === "open"
        ? <Picker week={week} rows={rows} teams={teams} entry={entry} save={save} now={now} />
        : (
          <Result
            week={week} phase={weekState.phase} entry={entry} teams={teams} rows={rows} onDone={done} current={current}
            onOpenWeek={(w) => navigate(w === current ? "/games/pick-three" : `/games/pick-three?week=${w}`)}
          />
        )}
    </GamePage>
  );
}

export default PickThreeGame;
